import { Layout } from 'antd'
import Image from 'next/image'
import type { CSSProperties, ReactNode } from 'react'

/**
 * Styles of the centered page and card, as the ui has always drawn its login and registration
 * screens. React hoists the `<style>` into the head once, whichever component renders it first,
 * so the package needs no CSS bundling in the apps.
 */
const CSS = `
.tucan-center-page.ant-layout {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  min-height: 100vh;
  padding-bottom: 30px;
  padding-top: 30px;
  width: 100vw;
}
.tucan-center-page--background.ant-layout {
  background-size: cover;
}
.tucan-center-home {
  left: 0;
  padding: 18px;
  position: absolute;
  top: 0;
}
.tucan-center-home img {
  display: block;
  height: 38px;
  width: auto;
}
.tucan-center-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  padding: 72px;
  width: 650px;
  z-index: 10;
}
.tucan-center-card .tucan-center-logo {
  display: block;
  height: 80px;
  margin: 0 auto 40px;
  width: auto;
}
.tucan-center-card h1 {
  color: #181818;
  font-size: 27px;
  font-weight: bold;
  letter-spacing: -0.01em;
  line-height: 38px;
  margin-bottom: 40px;
  text-align: center;
}
.tucan-center-card h1 small {
  color: #747474;
  display: block;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: -0.02em;
  line-height: 22px;
  margin-top: 16px;
}
@media (max-width: 800px) {
  .tucan-center-card { padding: 40px; }
}
@media (max-width: 600px) {
  .tucan-center-card { max-width: 95%; padding: 20px; }
}
@media (max-width: 450px) {
  .tucan-center-card { border-radius: 0; max-width: 100%; }
}
`

/** The card's stylesheet; rendered by both components, React keeps one copy. */
const CenterStyles = () => (
  <style href="tucan-login-center" precedence="default">
    {CSS}
  </style>
)

/**
 * Logo image. Logos are runtime URLs of any size (`LOGO_URL`, a company's logo), so the image is
 * not optimized and its height comes from the stylesheet, its width from its aspect ratio.
 */
const Logo = ({ src, alt, className }: { src: string; alt: string; className?: string }) => (
  <Image className={className} src={src} alt={alt} width={0} height={0} unoptimized />
)

/** Props of {@link CenterPage}. */
export interface CenterPageProps {
  /** URL of a background image covering the page. */
  background?: string
  children: ReactNode
}

/** Full-height page that centers its content, e.g. a {@link CenterCard}. */
export function CenterPage({ background, children }: CenterPageProps) {
  return (
    <Layout
      className={
        background ? 'tucan-center-page tucan-center-page--background' : 'tucan-center-page'
      }
      style={background ? { backgroundImage: `url(${background})` } : undefined}
    >
      <CenterStyles />
      {children}
    </Layout>
  )
}

/** Props of {@link CenterCard}. */
export interface CenterCardProps {
  /** Image above the card's content. */
  logo: string
  logoAlt?: string
  /** Image of the link home in the top-left corner; none without it. */
  icon?: string
  /** Target of the home link (default `/`). */
  homeHref?: string
  cardStyle?: CSSProperties
  /** Content; an `<h1>` (with an optional `<small>` subtitle) is styled as the card's heading. */
  children: ReactNode
}

/** White card with the logo on top, and a home link in the page's top-left corner. */
export function CenterCard({
  logo,
  logoAlt = 'tucan.ai',
  icon,
  homeHref = '/',
  cardStyle,
  children,
}: CenterCardProps) {
  return (
    <>
      <CenterStyles />
      {icon && (
        <a className="tucan-center-home" href={homeHref}>
          <Logo src={icon} alt="tucan.ai" />
        </a>
      )}
      <div className="tucan-center-card" style={cardStyle}>
        <Logo className="tucan-center-logo" src={logo} alt={logoAlt} />
        {children}
      </div>
    </>
  )
}
